import React from 'react';
import * as Yup from 'yup';
import { Box, Divider, IconButton, InputBase, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/SendRounded';
import DeleteIcon from '@mui/icons-material/CancelSharp';
import AttachmentIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { ScrollableView } from '../../Layout';
import { useSendUserMessageMutation } from '@uplift/queries';
import { Color } from '../types';
import { Header } from './Header.component';
import { MessagesContainer } from './MessagesList.component';
import { Conversation, uploadFileToS3, UploadPurpose } from '@uplift/api';
import { UserRole } from '@uplift/types';
import { FieldArray, ArrayHelpers, Formik, useField, useFormikContext } from 'formik';
import { Loader } from '../../Loader';
import { Tooltip } from '../../Tooltip';
import { useAlert } from '../../AlertProvider';
import { MessagingNotice } from './MessagingNotice.component';
import { OutOfOfficeNotice } from './OutOfOfficeNotice.component';


const maxFileSizeBytes = 20 * 1_048_576;

export type MessagesProps = {
  color?: Color;
  conversation?: Conversation;
  includeToDos?: boolean;
};


type Attachment = {
  id: string;
  name: string;
};


const UploadAttachmentsButton: React.FC<{ name: string; disabled: boolean }> = ({ name, disabled }) => {
  const alert = useAlert();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [ attachmentsIsUploading, setAttachmentsIsUploading ] = React.useState(false);

  return (
    <>
      <FieldArray
        name={name} render={(arrayHelpers: ArrayHelpers<Attachment[]>) => (
          <input
            ref={fileInputRef}
            type="file"
            multiple={true}
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={async e => {
              setAttachmentsIsUploading(true);
              if (e.target.files?.length) {
                const files = Array.from(e.target.files);

                e.target.value = '';
                for await (const file of files) {
                  try {
                    const fileSizeBytes = file.size;

                    if (fileSizeBytes > maxFileSizeBytes) {
                      alert.error(`File ${file.name} exceeds the maximum size of 20MB.`);
                      continue;
                    }

                    const uploadedFile = await uploadFileToS3({ purpose: UploadPurpose.messagingAttachment, file: file });

                    arrayHelpers.push({
                      id: uploadedFile.file_upload.id,
                      name: uploadedFile.file_upload.name,
                    });
                  } catch (error) {
                    console.error(error);
                    if (error instanceof Error) {
                      alert.error(error.message);
                    }
                  }
                }
              }
              setAttachmentsIsUploading(false);
            }}
          />
        )}
      />
      <Tooltip
        title={(
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
            <span>Add attachment (images or PDFs)</span>
            <span>Max 20MB</span>
          </Box>
        )}
        placement="top"
      >
        <IconButton
          data-testid="add-attachment-button"
          disabled={disabled}
          loading={attachmentsIsUploading}
          onClick={() => {
            if (attachmentsIsUploading) return;
            fileInputRef.current?.click();
          }}
        >
          <AttachmentIcon sx={{ fontSize: '1.4rem' }} />
        </IconButton>
      </Tooltip>
    </>

  );
};


const MessageInput: React.FC<{ conversation: Conversation; color?: Color }> = ({ conversation, color }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const formikContext = useFormikContext();
  const [ field ] = useField<string>('message');

  const placeholder = React.useMemo(() => {
    if (conversation.disabled) {
      return 'You are not currently matched with this user';
    }
    return 'Write your message...';
  }, [ conversation.disabled ]);

  return (
    <Box sx={{ borderRadius: '4px', bgcolor: 'grey.100' }}>
      <InputBase
        data-testid="message-input"
        inputRef={inputRef}
        disabled={conversation.disabled}
        {...field}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (formikContext.isSubmitting) {
              return;
            }
            formikContext.handleSubmit();
          }
        }}
        color={color}
        multiline
        maxRows={5}
        minRows={2}
        fullWidth
        placeholder={placeholder}
        sx={{ px: 2, pt: 1.5, pb: 0.5 }}
        slotProps={{
          input: {
            enterKeyHint: 'send',
            maxLength: 1000,
          }
        }}
      />
      <Box sx={{ px: 1, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box flex={1} />
        <UploadAttachmentsButton name="attachments" disabled={conversation.disabled} />
        <IconButton
          loading={formikContext.isSubmitting}
          disabled={conversation.disabled || !formikContext.isValid}
          size="small"
          color={color}
          data-testid="send-button"
          sx={{ p: '7px' }}
          onClick={() => {
            if (!formikContext.isSubmitting) {
              formikContext.handleSubmit();
              inputRef.current?.focus();
            }
          }}
        >
          <SendIcon sx={{ fontSize: '1.55rem' }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export const AttachmentItem: React.FC<{ name: string; onClick?: () => Promise<void>; onDelete?: () => void }> = ({ name, onClick, onDelete }) => {
  const [ isLoading, setIsLoading ] = React.useState(false);

  const cursor = React.useMemo(() => {
    if (isLoading) return 'wait';
    if (onClick) return 'pointer';
    return 'default';
  }, [ isLoading, onClick ]);

  return (
    <Box
      data-testid="attachment-item"
      sx={{
        p: 1,
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        flex: 1,
        boxSizing: 'border-box',
        cursor: cursor,
        userSelect: 'none',
        '&:hover': onClick ? { backgroundColor: 'grey.100' } : undefined,
      }}
      onClick={async () => {
        if (isLoading || !onClick) return;
        setIsLoading(true);
        await onClick();
        setIsLoading(false);
      }}
    >
      {isLoading ? <Loader size={24} width="24px" height="24px" sx={{ fontSize: 24 }} /> : <AttachmentIcon sx={{ fontSize: 24 }} />}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
      }}
      >
        <Typography
          variant="body2" color="textSecondary" component="span"
          sx={{ flex: 1, whiteSpace: 'pre-wrap', textOverflow: 'ellipsis' }}
        >
          {name}
        </Typography>
        {onDelete && <DeleteIcon onClick={onDelete} fontSize="small" sx={{ ml: 1, cursor: 'pointer', color: 'grey.400', '&:hover': { color: 'grey.600' } }} />}
      </Box>
    </Box>
  );
};


export const Messages: React.FC<MessagesProps> = ({
  conversation,
  color = 'secondary',
  includeToDos = false,
}) => {
  const sendMessageMutation = useSendUserMessageMutation();

  if (!conversation) {
    return (
      <MessagingNotice />
    );
  }

  return (
    <Box flex={1} display="flex" flexDirection="column">
      <Box>
        <Header color={color} conversation={conversation} />
      </Box>
      <Divider />
      {conversation.participantRole === UserRole.provider && <OutOfOfficeNotice providerId={conversation.participantId} />}
      <Box flex={1}>
        <ScrollableView width="100%" height="100%">
          <React.Suspense fallback={<Loader sx={{ height: '100%', width: '100%' }} />}>
            <MessagesContainer color={color} conversationId={conversation.id} includeToDos={includeToDos} />
          </React.Suspense>
        </ScrollableView>
      </Box>
      <Divider />
      <Formik
        isInitialValid={false}
        initialValues={{
          message: '',
          attachments: [],
        }}
        validationSchema={Yup.object().shape({
          message: Yup.string().nullable().when('attachments', {
            is: (attachments: Attachment[]) => !attachments.length,
            then: schema => schema.min(1).required('Message is required'),
          }),
          attachments: Yup.array().of(Yup.object().shape({
            id: Yup.string().uuid().required(),
            name: Yup.string().required(),
          })),
        })}
        onSubmit={async (values: { message: string; attachments: Attachment[] }, helpers) => {
          await sendMessageMutation.mutateAsync({
            conversationId: conversation.id,
            message: values.message.trim(),
            attachmentIds: values.attachments.map(attachment => attachment.id),
          });
          helpers.resetForm();
        }}
      >
        {formik => (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!!formik.values.attachments?.length && (
              <Box>
                <FieldArray
                  name="attachments"
                  render={(arrayHelpers: ArrayHelpers<Attachment[]>) => (
                    <Box sx={{ border: '1px solid', borderColor: 'grey.300', maxWidth: '70%' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {formik.values.attachments.map((attachment, index) => (
                          <React.Fragment key={attachment.id}>
                            {!!index && <Divider />}
                            <AttachmentItem
                              key={attachment.id}
                              name={attachment.name}
                              onDelete={() => {
                                arrayHelpers.remove(index);
                              }}
                            />
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  )}
                />
              </Box>
            )}
            <MessageInput conversation={conversation} color={color} />
          </Box>
        )}
      </Formik>
    </Box>
  );
};
